import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguages } from '../hooks/useLanguages';
import { lookupWord } from '../services/wordLookup';
import { BookmarksService } from '../services/BookmarksService';

const WORD_RE = /[\p{L}\p{M}\p{N}'-]+/gu;

// Splits text into alternating word / non-word tokens — mirrors the mobile
// app's _tokenize in widgets/tappable_text.dart exactly (same regex).
function tokenize(text) {
  const tokens = [];
  let last = 0;
  for (const match of text.matchAll(WORD_RE)) {
    if (match.index > last) tokens.push({ text: text.slice(last, match.index), isWord: false });
    tokens.push({ text: match[0], isWord: true });
    last = match.index + match[0].length;
  }
  if (last < text.length) tokens.push({ text: text.slice(last), isWord: false });
  return tokens;
}

let synth = null;
function getSynth() {
  if (synth === null) synth = typeof window !== 'undefined' && 'speechSynthesis' in window ? window.speechSynthesis : false;
  return synth || null;
}
function speak(ttsCode, text) {
  const engine = getSynth();
  if (!engine || !text) return;
  try {
    engine.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = ttsCode || '';
    utterance.rate = 0.95;
    utterance.onerror = () => {};
    engine.speak(utterance);
  } catch {
    // Never let a speak button crash the page.
  }
}

// Renders `text` (assumed to be in targetCode) with every word individually
// tappable — tapping shows a translation lookup popover backed by
// wordLookup.js, with play-audio and save-to-bookmark actions. Mirrors the
// mobile app's TappableText widget.
export default function TappableText({ text, nativeCode, targetCode, className = '', style }) {
  const { languages } = useLanguages();
  const lang = languages.find((l) => l.code === targetCode) || { code: targetCode, dir: 'ltr', tts: '' };
  const [active, setActive] = useState(null); // { word } | null

  const tokens = tokenize(text || '');

  return (
    <>
      <span dir={lang.dir || 'ltr'} className={className} style={style}>
        {tokens.map((token, index) => (token.isWord ? (
          <span
            // eslint-disable-next-line react/no-array-index-key
            key={index}
            role="button"
            tabIndex={0}
            onClick={() => setActive({ word: token.text })}
            onKeyDown={(e) => { if (e.key === 'Enter') setActive({ word: token.text }); }}
            className="cursor-pointer rounded hover:bg-indigo-100"
          >
            {token.text}
          </span>
        // eslint-disable-next-line react/no-array-index-key
        ) : <span key={index}>{token.text}</span>))}
      </span>
      {active && (
        <LookupSheet
          word={active.word}
          lang={lang}
          nativeCode={nativeCode}
          targetCode={targetCode}
          onClose={() => setActive(null)}
        />
      )}
    </>
  );
}

function bookmarkId(nativeCode, targetCode, word) {
  return `vocab_${nativeCode}_${targetCode}_word_${word.toLowerCase()}`;
}

function LookupSheet({ word, lang, nativeCode, targetCode, onClose }) {
  const { t } = useTranslation();
  const [translation, setTranslation] = useState(undefined); // undefined = loading
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    let active = true;
    lookupWord(nativeCode, targetCode, word).then((result) => {
      if (!active) return;
      setTranslation(result);
      setSaved(BookmarksService.isBookmarked(`${nativeCode}_${targetCode}`, bookmarkId(nativeCode, targetCode, word)));
    });
    return () => { active = false; };
  }, [nativeCode, targetCode, word]);

  function toggleSave() {
    if (!translation) return;
    const added = BookmarksService.toggle({
      id: bookmarkId(nativeCode, targetCode, word),
      type: 'vocab',
      langPair: `${nativeCode}_${targetCode}`,
      nativeText: translation,
      targetText: word,
    });
    setSaved(added);
  }

  const loading = translation === undefined;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 backdrop-blur-sm sm:items-center"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-sm rounded-t-3xl bg-white p-5 pb-8 shadow-2xl sm:rounded-2xl sm:pb-5"
      >
        <div className="flex items-start justify-between">
          <p dir={lang.dir || 'ltr'} className="text-2xl font-black text-slate-900">{word}</p>
          <button type="button" onClick={onClose} aria-label={t('common.cancel')} className="text-slate-400 hover:text-slate-600">✕</button>
        </div>
        <p className="mt-1.5 text-[11px] font-bold uppercase tracking-wide text-slate-400">{t('wordLookup.translationLabel')}</p>
        {loading ? (
          <div className="mt-1 h-6 w-32 animate-pulse rounded bg-slate-100" />
        ) : (
          <p className={`mt-0.5 text-lg font-bold ${translation ? 'text-indigo-700' : 'text-slate-500'}`}>
            {translation || t('wordLookup.notInDictionary')}
          </p>
        )}
        <div className="mt-4 flex gap-2">
          <button
            type="button"
            onClick={() => speak(lang.tts, word)}
            className="flex items-center gap-1.5 rounded-xl border border-slate-200 px-3.5 py-2 text-sm font-bold text-slate-700 hover:bg-slate-50"
          >
            🔊 {t('exercise.playAudio')}
          </button>
          {!loading && translation && (
            <button
              type="button"
              onClick={toggleSave}
              className="flex items-center gap-1.5 rounded-xl border border-slate-200 px-3.5 py-2 text-sm font-bold text-slate-700 hover:bg-slate-50"
            >
              {saved ? '🔖' : '📑'} {t('common.save')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
